import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { ProcessProgressEvent, ProcessProgressStatus } from '../types/processProgress';

interface ProcessProgressModalProps {
  open: boolean;
  refId: string;
  events: ProcessProgressEvent[];
  error: string | null;
  onClose?: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  started: 'Starting request',
  fetching_gcs_files: 'Loading OCR files',
  loading_database_records: 'Loading database records',
  processing_gcs_record: 'Processing GCS records',
  inserting_bigquery: 'Saving GCS records',
  processing_db_record: 'Processing database records',
  updating_firestore_metadata: 'Updating metadata',
  training_model: 'Training similarity model',
  calculating_ocr_similarity: 'Calculating OCR similarity',
  record_blacklist_check: 'Checking blacklist',
  record_similarity_search: 'Searching similar documents',
  record_gateway_update: 'Updating gateway',
  complete: 'Completed',
  error: 'Failed',
};

function formatStageLabel(stage: string): string {
  return STAGE_LABELS[stage] || stage.replace(/_/g, ' ');
}

function statusLabel(status: ProcessProgressStatus): string {
  switch (status) {
    case 'done':
      return 'Done';
    case 'error':
      return 'Error';
    case 'running':
      return 'Running';
    default:
      return 'Pending';
  }
}

function statusColor(status: ProcessProgressStatus): 'default' | 'primary' | 'success' | 'error' {
  switch (status) {
    case 'done':
      return 'success';
    case 'error':
      return 'error';
    case 'running':
      return 'primary';
    default:
      return 'default';
  }
}

const ProcessProgressModal: React.FC<ProcessProgressModalProps> = ({
  open,
  refId,
  events,
  error,
  onClose,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!open) {
      setElapsedSeconds(0);
      return undefined;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [open]);

  const latestEvent = events[events.length - 1];
  const isFinished = Boolean(error) || latestEvent?.stage === 'complete';
  const progressPercent = useMemo(() => {
    if (!latestEvent?.progress || latestEvent.progress.total <= 0) {
      return undefined;
    }

    return Math.round((latestEvent.progress.current / latestEvent.progress.total) * 100);
  }, [latestEvent]);

  return (
    <Dialog
      open={open}
      onClose={isFinished ? onClose : undefined}
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown={!isFinished}
    >
      <DialogTitle>
        Processing Ref ID {refId}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Elapsed: {Math.floor(elapsedSeconds / 60)}m {elapsedSeconds % 60}s
            </Typography>
            {latestEvent && (
              <Chip
                size="small"
                label={formatStageLabel(latestEvent.stage)}
                color={error ? 'error' : isFinished ? 'success' : 'primary'}
              />
            )}
          </Box>
          <LinearProgress
            variant={progressPercent !== undefined ? 'determinate' : 'indeterminate'}
            value={progressPercent ?? 0}
            sx={{ height: 8, borderRadius: 1 }}
          />
          {progressPercent !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {progressPercent}% complete
            </Typography>
          )}
        </Box>

        {latestEvent && !error && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary">
              {latestEvent.message}
            </Typography>
            {latestEvent.detail && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {latestEvent.detail}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="error.contrastText">
              {error}
            </Typography>
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Progress log
        </Typography>
        <List dense sx={{ maxHeight: 320, overflow: 'auto', bgcolor: 'background.paper' }}>
          {events.map((event, index) => (
            <ListItem key={`${event.stage}-${event.timestamp}-${index}`} alignItems="flex-start">
              <Chip
                size="small"
                label={statusLabel(event.status)}
                color={statusColor(event.status)}
                sx={{ mr: 1.5, mt: 0.25, minWidth: 72 }}
              />
              <ListItemText
                primary={event.message}
                secondary={
                  <>
                    <Typography component="span" variant="caption" color="text.secondary" display="block">
                      {formatStageLabel(event.stage)}
                      {event.progress ? ` · ${event.progress.current}/${event.progress.total}` : ''}
                      {' · '}
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </Typography>
                    {event.detail && (
                      <Typography component="span" variant="body2" color="text.secondary" display="block">
                        {event.detail}
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessProgressModal;
